import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { standaloneFdpQueryKeys } from '@teetsh/app/src/queries/fdp/useStandaloneFicheDePrep';
import { SEQUENCES_LIST_KEY } from '@teetsh/app/src/queries/sequences/useSequencesFolders';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';

interface MoveFdpPayload {
  targetSequenceId?: string;
  targetFolderId?: string;
}

/**
 * Move a fiche de prep to a different sequence or folder
 * If targetSequenceId is provided, the fiche de prep will be moved to that sequence
 * If targetFolderId is provided, the fiche de prep will be moved to that folder
 */
function moveFicheDePrep(ficheDePrepId: string, payload: MoveFdpPayload) {
  return request(`/fiches-de-prep/${ficheDePrepId}/move`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Hook for moving a standalone fiche de prep to a different sequence or folder
 */
export function useMoveStandaloneFicheDePrep() {
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotification();
  const { t } = useTranslation(['sequence']);

  return useMutation({
    mutationFn: ({
      ficheDePrepId,
      targetSequenceId,
      targetFolderId,
    }: {
      ficheDePrepId: string;
      targetSequenceId?: string;
      targetFolderId?: string;
    }) => {
      const payload: MoveFdpPayload = {};

      if (targetSequenceId) {
        payload.targetSequenceId = targetSequenceId;
      }

      if (targetFolderId) {
        payload.targetFolderId = targetFolderId;
      }

      return moveFicheDePrep(ficheDePrepId, payload);
    },
    onSuccess: (_, variables) => {
      // Invalidate the standalone fiche de prep query
      queryClient.invalidateQueries({
        queryKey: standaloneFdpQueryKeys.detail(variables.ficheDePrepId),
      });

      // Invalidate the sequences list to update the UI
      queryClient.invalidateQueries({
        queryKey: [SEQUENCES_LIST_KEY],
      });

      notifySuccess({
        title: t('sequence:messages.fdp.moveSuccess'),
      });
    },
    onError: () => {
      notifyError({
        title: t('sequence:messages.fdp.moveError'),
      });
    },
  });
}
